<template>
  <div class="menu-tree-view">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Gestión de Menús - Estilo 3</h1>
      <div class="d-flex gap-2">
        <button @click="exportToExcel" class="btn btn-success">
          <i class="bi bi-file-earmark-excel me-1"></i> Exportar a Excel
        </button>
        <button @click="addMenu" class="btn btn-primary">
          <i class="bi bi-plus-circle me-1"></i> Nuevo Menú
        </button>
      </div>
    </div>
    
    <!-- Filtros -->
    <div class="card mb-4 bg-light">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <div class="form-group">
              <label for="filterTitulo">Título</label>
              <input 
                type="text" 
                id="filterTitulo" 
                v-model="filters.titulo" 
                class="form-control" 
                placeholder="Buscar por título"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label for="filterEstado">Estado</label>
              <select id="filterEstado" v-model="filters.estado" class="form-select">
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label for="viewType">Tipo de Vista</label>
              <select id="viewType" v-model="viewType" class="form-select">
                <option value="tree">Vista de Árbol</option>
                <option value="list">Vista de Lista</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Alertas -->
    <div class="alert alert-info" v-if="loading">Cargando menús...</div>
    <div class="alert alert-danger" v-if="error">{{ error }}</div>
    
    <!-- Vista de árbol -->
    <div v-if="!loading && !error && viewType === 'tree'" class="card shadow">
      <div class="card-body">
        <div v-if="treeMenus.length === 0" class="alert alert-warning">
          No se encontraron menús con los filtros aplicados
        </div>
        
        <ul class="menu-tree">
          <li v-for="menu in treeMenus" :key="menu.Menu_id" class="menu-tree-item">
            <div class="menu-tree-content" :class="{'inactive': !menu.Es_activo}">
              <div class="menu-info">
                <span class="menu-title">{{ menu.Titulo }}</span>
                <span class="menu-badge" :class="menu.Es_activo ? 'active' : 'inactive'">
                  {{ menu.Es_activo ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
              <div class="menu-actions">
                <button @click="editMenu(menu)" class="btn btn-sm btn-warning me-1">
                  <i class="bi bi-pencil"></i>
                </button>
                <button @click="confirmDelete(menu)" class="btn btn-sm btn-danger">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            
            <!-- Menús hijos (recursivos) -->
            <ul v-if="getChildMenus(menu.Menu_id).length > 0" class="menu-tree-children">
              <li v-for="childMenu in getChildMenus(menu.Menu_id)" :key="childMenu.Menu_id" class="menu-tree-item">
                <div class="menu-tree-content" :class="{'inactive': !childMenu.Es_activo}">
                  <div class="menu-info">
                    <span class="menu-title">{{ childMenu.Titulo }}</span>
                    <span class="menu-badge" :class="childMenu.Es_activo ? 'active' : 'inactive'">
                      {{ childMenu.Es_activo ? 'Activo' : 'Inactivo' }}
                    </span>
                  </div>
                  <div class="menu-actions">
                    <button @click="editMenu(childMenu)" class="btn btn-sm btn-warning me-1">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button @click="confirmDelete(childMenu)" class="btn btn-sm btn-danger">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
    
    <!-- Vista de lista avanzada -->
    <div v-if="!loading && !error && viewType === 'list'" class="card shadow">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-striped mb-0 advanced-table">
            <thead>
              <tr>
                <th @click="sortBy('Menu_id')" class="sortable">
                  ID <i :class="getSortIcon('Menu_id')"></i>
                </th>
                <th @click="sortBy('Titulo')" class="sortable">
                  Título <i :class="getSortIcon('Titulo')"></i>
                </th>
                <th @click="sortBy('Descripcion')" class="sortable">
                  Descripción <i :class="getSortIcon('Descripcion')"></i>
                </th>
                <th @click="sortBy('Enlace')" class="sortable">
                  Enlace <i :class="getSortIcon('Enlace')"></i>
                </th>
                <th @click="sortBy('Es_activo')" class="sortable">
                  Estado <i :class="getSortIcon('Es_activo')"></i>
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredMenus.length === 0">
                <td colspan="6" class="text-center py-4">
                  No se encontraron menús con los filtros aplicados
                </td>
              </tr>
              <template v-for="menu in filteredMenus" :key="menu.Menu_id">
                <tr @click="toggleDetails(menu)" class="clickable-row" :class="{'table-active': menu.showDetails}">
                  <td>{{ menu.Menu_id }}</td>
                  <td>
                    <i v-if="hasChildren(menu.Menu_id)" class="bi bi-diagram-3 me-1 text-primary"></i>
                    {{ menu.Titulo }}
                  </td>
                  <td>{{ truncateText(menu.Descripcion, 50) }}</td>
                  <td>{{ truncateText(menu.Enlace, 30) }}</td>
                  <td>
                    <span :class="menu.Es_activo ? 'badge bg-success' : 'badge bg-danger'">
                      {{ menu.Es_activo ? 'Activo' : 'Inactivo' }}
                    </span>
                  </td>
                  <td>
                    <div class="btn-group">
                      <button @click.stop="editMenu(menu)" class="btn btn-sm btn-warning me-1">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button @click.stop="confirmDelete(menu)" class="btn btn-sm btn-danger">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="menu.showDetails" class="details-row">
                  <td colspan="6">
                    <div class="row p-3">
                      <div class="col-md-6">
                        <h6>Detalles del Menú</h6>
                        <table class="table table-sm">
                          <tbody>
                            <tr>
                              <th>Menú Padre</th>
                              <td>{{ menu.Menu_id_padre || 'Ninguno' }}</td>
                            </tr>
                            <tr>
                              <th>Orden</th>
                              <td>{{ menu.Orden }}</td>
                            </tr>
                            <tr>
                              <th>Menú Superior</th>
                              <td>{{ menu.Es_top ? 'Sí' : 'No' }}</td>
                            </tr>
                            <tr>
                              <th>Grupo</th>
                              <td>{{ menu.Grupo || 'No asignado' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <h6>Menús Hijos</h6>
                        <div v-if="getChildMenus(menu.Menu_id).length === 0" class="text-muted">
                          Este menú no tiene elementos hijos
                        </div>
                        <ul v-else class="list-group">
                          <li v-for="child in getChildMenus(menu.Menu_id)" :key="child.Menu_id" class="list-group-item d-flex justify-content-between align-items-center">
                            {{ child.Titulo }}
                            <span :class="child.Es_activo ? 'badge bg-success' : 'badge bg-danger'">
                              {{ child.Es_activo ? 'Activo' : 'Inactivo' }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <!-- Modal de confirmación -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="menuToDelete">
            ¿Está seguro que desea eliminar el menú "{{ menuToDelete.Titulo }}"?
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteMenu">Eliminar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export default {
  name: 'MenuTableView3',
  setup() {
    const { requiresAuth, requiresMenuPermission } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: true
    })

    return {
      requiresAuth,
      requiresMenuPermission
    }
  },
  data() {
    return {
      menus: [],
      loading: true,
      error: null,
      menuToDelete: null,
      sortKey: 'Menu_id',
      sortDir: 'asc',
      viewType: 'tree',
      filters: {
        titulo: '',
        estado: ''
      }
    };
  },
  computed: {
    filteredMenus() {
      let result = [...this.menus];
      
      // Aplicar filtros
      if (this.filters.titulo) {
        result = result.filter(menu => 
          menu.Titulo && menu.Titulo.toLowerCase().includes(this.filters.titulo.toLowerCase())
        );
      }
      
      if (this.filters.estado !== '') {
        const estado = this.filters.estado === 'true';
        result = result.filter(menu => menu.Es_activo === estado);
      }
      
      // Aplicar ordenamiento
      result.sort((a, b) => {
        let aValue = a[this.sortKey];
        let bValue = b[this.sortKey];
        
        // Manejar valores nulos
        if (aValue === null) aValue = '';
        if (bValue === null) bValue = '';
        
        // Ordenar según tipo de dato
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }
        
        if (this.sortDir === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
      
      // Agregar propiedad para mostrar/ocultar detalles
      return result.map(menu => ({
        ...menu,
        showDetails: false
      }));
    },
    
    treeMenus() {
      // Obtener solo los menús de nivel superior (sin padre o con padre nulo)
      return this.filteredMenus.filter(menu => !menu.Menu_id_padre);
    }
  },
  created() {
    this.fetchMenus();
  },
  methods: {
    fetchMenus() {
      this.loading = true;
      this.error = null;
      
      axios.post(getApiUrl('/api/Menu/listar'))
        .then(response => {
          this.menus = response.data.OData;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error al cargar menús:', error);
          this.error = 'No se pudieron cargar los menús. Por favor, intente nuevamente.';
          this.loading = false;
        });
    },
    
    sortBy(key) {
      // Si hacemos clic en la misma columna, invertimos la dirección
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    
    getSortIcon(key) {
      if (this.sortKey !== key) return 'bi bi-arrow-down-up text-muted';
      return this.sortDir === 'asc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
    },
    
    getChildMenus(parentId) {
      return this.filteredMenus.filter(menu => menu.Menu_id_padre === parentId);
    },
    
    hasChildren(menuId) {
      return this.menus.some(menu => menu.Menu_id_padre === menuId);
    },
    
    toggleDetails(menu) {
      menu.showDetails = !menu.showDetails;
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    
    addMenu() {
      this.$router.push('/menus/add');
    },
    
    editMenu(menu) {
      this.$router.push(`/menus/edit/${menu.Menu_id}`);
    },
    
    confirmDelete(menu) {
      this.menuToDelete = menu;
      // Mostrar modal de confirmación usando DOM nativo
      const modalEl = document.getElementById('deleteModal');
      if (modalEl) {
        modalEl.classList.add('show');
        modalEl.style.display = 'block';
        document.body.classList.add('modal-open');
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
      }
    },
    
    deleteMenu() {
      if (!this.menuToDelete) return;
      
      axios.post(getApiUrl('/api/Menu/borrar'), this.menuToDelete.Menu_id)
        .then(response => {
          // Ocultar modal usando DOM nativo
          const modalEl = document.getElementById('deleteModal');
          if (modalEl) {
            modalEl.classList.remove('show');
            modalEl.style.display = 'none';
            document.body.classList.remove('modal-open');
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) {
              document.body.removeChild(backdrop);
            }
          }
          
          // Actualizar lista
          this.fetchMenus();
          alert(response.data.message || 'Menú eliminado correctamente.');
        })
        .catch(error => {
          console.error('Error al eliminar menú:', error);
          alert('No se pudo eliminar el menú. Por favor, intente nuevamente.');
        });
    },
    
    exportToExcel() {
      // Crear un array con los datos a exportar
      const data = this.filteredMenus.map(menu => ({
        ID: menu.Menu_id,
        Título: menu.Titulo,
        Descripción: menu.Descripcion,
        Enlace: menu.Enlace,
        'Menú Padre': menu.Menu_id_padre,
        Orden: menu.Orden,
        Estado: menu.Es_activo ? 'Activo' : 'Inactivo',
        'Menú Superior': menu.Es_top ? 'Sí' : 'No'
      }));
      
      // Crear un blob y descargar como CSV
      let csvContent = 'data:text/csv;charset=utf-8,';
      
      // Agregar encabezados
      const headers = Object.keys(data[0] || {});
      csvContent += headers.join(',') + '\n';
      
      // Agregar filas
      data.forEach(item => {
        const row = headers.map(header => {
          const cell = item[header] || '';
          return `"${cell}"`;
        });
        csvContent += row.join(',') + '\n';
      });
      
      // Descargar archivo
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'menus.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
.menu-tree-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* Estilos para la vista de árbol */
.menu-tree {
  list-style: none;
  padding-left: 0;
}

.menu-tree-item {
  margin-bottom: 10px;
}

.menu-tree-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid var(--primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.menu-tree-content.inactive {
  border-left-color: #dc3545;
  opacity: 0.7;
}

.menu-tree-children {
  list-style: none;
  padding-left: 30px;
  margin-top: 10px;
  border-left: 1px dashed #dee2e6;
}

.menu-info {
  display: flex;
  align-items: center;
}

.menu-title {
  font-weight: 500;
  margin-right: 10px;
}

.menu-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 20px;
}

.menu-badge.active {
  background-color: #d1e7dd;
  color: #0f5132;
}

.menu-badge.inactive {
  background-color: #f8d7da;
  color: #842029;
}

/* Estilos para la vista de lista avanzada */
.advanced-table thead th {
  background-color: #343a40;
  color: white;
  padding: 12px;
  font-weight: 500;
  text-align: left;
  border-bottom: none;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #2c3136;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.details-row {
  background-color: #f8f9fa;
}

.details-row td {
  padding: 0;
}
</style>
